import { NgOptimizedImage } from "@angular/common";
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from "@angular/core";

import { ImageDestroyGuardDirective } from "./image.directive";
import { aspectRatioAttribute, computeDimensions } from "./image.helper";
import {
    ImageBackground,
    ImageExtend,
    ImageFit,
    ImageFormat,
    ImageResamplingMode,
} from "./image.types";
import { Scene7ImageLoaderParams } from "./scene7-loader/scene7-image-loader.model";
import { SCENE7_IMAGE_SETTINGS } from "./scene7-loader/scene7-image-settings";
import { provideScene7Loader } from "./scene7-loader/scene7-loader-providers";

@Component({
    selector: "h-image",
    imports: [NgOptimizedImage, ImageDestroyGuardDirective],
    templateUrl: "./image.component.html",
    styleUrl: "./image.component.scss",
    providers: [provideScene7Loader()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        "[class.fill]": "!isFixedSize()",
        "[class.fit-content]": "isFixedSize() || aspectRatio()",
        "[style.width.px]": "isFixedSize() ? width() : undefined",
        "[style.height.px]": "isFixedSize() ? height() : undefined",
        "[style.aspect-ratio]": "aspectRatio()",
    },
})
export class ImageComponent {
    private readonly settings = inject(SCENE7_IMAGE_SETTINGS);

    public readonly src = input.required<string>();

    public readonly fallbackSrc = input<string | boolean>(true);
    /**
     * Set image to be responsive by providing the rendered sizes list
     *
     * See https://angular.dev/guide/image-optimization#responsive-images
     * @example
     * <h-image [src]="/image.png" [sizes]="(max-width: 768px) 100vw, 50vw" />
     *
     * // That means:
     * // "I expect this image to be 100 percent of the screen width on devices under 768px wide.
     * // Otherwise, I expect it to be 50 percent of the screen width"
     * @see buildGridResponsiveSizes() function for building sizes descriptor
     */
    public readonly sizes = input<string>();
    /**
     * Fixed width of the image in pixels
     *
     * If neither width nor height is set, the image will _fill_ the container
     */
    public readonly width = input<number>();
    /**
     * Fixed height of the image in pixels
     *
     * If neither width nor height is set, the image will _fill_ the container
     */
    public readonly height = input<number>();
    public readonly maxWidth = input<number>();
    public readonly maxHeight = input<number>();
    public readonly aspectRatio = input(undefined, {
        transform: aspectRatioAttribute,
    });
    /**
     * Aspect ratio used only to pick the default fallback image.
     *
     * Useful when the image has no `aspectRatio`
     * but the fallback must still match a given ratio.
     * Defaults to `aspectRatio`.
     */
    public readonly fallbackAspectRatio = input(undefined, {
        transform: aspectRatioAttribute,
    });
    /**
     * Alt text of the image (required)
     *
     * To define absence of alt text, explicitly set `alt=""` or `[alt]="null"` (which
     * both have **different** meanings)
     */
    public readonly alt = input.required<string>();
    public readonly priority = input<boolean>();
    public readonly loading = input<"lazy" | "eager" | "auto">();
    public readonly decoding = input<"sync" | "async" | "auto">();
    public readonly upscale = input(true, {
        transform: booleanAttribute,
    });
    /**
     * Image background, only applicable when contributed image has transparency
     *
     * Default is `"auto"`.
     */
    public readonly background = input<ImageBackground | undefined>("auto");
    public readonly fit = input<ImageFit | undefined>("fit");
    public readonly placeholder = input(false, {
        transform: booleanAttribute,
    });
    public readonly quality = input<number>();
    public readonly format = input<ImageFormat>();
    public readonly extend = input<ImageExtend>();
    public readonly sharpen = input(false, {
        transform: booleanAttribute,
    });
    public readonly resamplingMode = input<ImageResamplingMode>();
    /**
     * Whether to keep parameters affecting image rendering quality already
     * present in source url.
     *
     * This is `false` by default, meaning that parameters affecting image
     * rendering quality are stripped out. This includes:
     * - wid
     * - hei
     * - size
     * - fit
     * - qlt
     * - fmt
     * - op_sharpen
     * - resMode
     * - bgc
     * - bgColor
     *
     * Set this option to `true` to keep such parameters.
     *
     * Parameters not listed here, such as `align` or `extend`, are never
     * stripped out unless overridden explicitly with dedicated options.
     */
    public readonly keepSourceParams = input<boolean>(false);

    public readonly imageLoaded = output<void>();
    public readonly imageError = output<void>();

    protected readonly isFixedSize = computed(
        () => this.width() !== undefined || this.height() !== undefined,
    );

    protected readonly renderedDimensions = computed(() =>
        this.isFixedSize()
            ? computeDimensions(this.width(), this.height(), this.aspectRatio())
            : undefined,
    );

    protected readonly loaderParams = computed((): Scene7ImageLoaderParams => ({
        aspectRatio: this.aspectRatio(),
        fit: this.fit(),
        upscale: this.upscale(),
        background: this.background(),
        height: this.height(),
        quality: this.quality(),
        extend: this.extend(),
        sharpen: this.sharpen(),
        format: this.format(),
        maxWidth: this.maxWidth(),
        maxHeight: this.maxHeight(),
        keepSourceParams: this.keepSourceParams(),
        resamplingMode: this.resamplingMode(),
    }));

    protected readonly fallbackLoaderParams = computed(
        (): Scene7ImageLoaderParams => ({
            aspectRatio: this.resolvedFallbackAspectRatio(),
            fit: "fit",
            upscale: true,
            background: this.background(),
            height: this.height(),
            quality: this.quality(),
            maxWidth: this.maxWidth(),
            maxHeight: this.maxHeight(),
        }),
    );

    private readonly resolvedFallbackAspectRatio = computed(
        () => this.aspectRatio() ?? this.fallbackAspectRatio(),
    );
    protected readonly hasLoadError = signal(false);

    protected readonly renderedFallbackSrc = computed(() => {
        const fallbackSource = this.fallbackSrc();

        // When a non-empty ("") alt is provided, never display fallback image
        if (!fallbackSource || this.alt()) {
            return undefined;
        }

        if (typeof fallbackSource === "string") {
            return fallbackSource;
        }

        const defaultFallbackMap = this.settings.defaultFallbackImageSrc;
        const aspectRatio = this.resolvedFallbackAspectRatio();

        if (aspectRatio && defaultFallbackMap[aspectRatio]) {
            return defaultFallbackMap[aspectRatio];
        }

        return defaultFallbackMap.default;
    });

    constructor() {
        effect(() => {
            this.src();
            this.hasLoadError.set(false);
        });
    }

    protected onLoadError() {
        this.hasLoadError.set(true);
        this.imageError.emit();
    }
}
